import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Directive to convert between an array of strings in the form model and a char-separated string in the input field.
 * Usage: Add `mcArrayToString` attribute to any `<input matInput>` element that is bound to an array of strings.
 * The separator can be customized using the `mcArrayToStringSeparator` input property.
 * Example: `<input matInput formControlName="tags" mcArrayToString mcArrayToStringSeparator="; ">`
 */
@Directive({
  selector: 'input[matInput][mcArrayToString]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ArrayToStringDirective),
      multi: true
    }
  ]
})
export class ArrayToStringDirective implements ControlValueAccessor {

  /** The separator used when joining array values for display and splitting input back to an array.
   * Default is `', '`.
   */
  @Input() mcArrayToStringSeparator: string = ', ';

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) { }

  /** Form Model -> DOM View (Converts Array to String) */
  writeValue(value: any[] | null): void {
    const normalizedValue = Array.isArray(value) ? value.join(this.mcArrayToStringSeparator) : '';
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', normalizedValue);
  }

  /** DOM View -> Form Model (Converts String back to Array on input change) */
  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    const separator = this.mcArrayToStringSeparator.trim() || ',';
    const arrayValue = value
      ? value.split(separator).map(item => item.trim()).filter(item => item !== '')
      : [];
    this.onChange(arrayValue);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /** Reflects the form control's disabled state onto the native input. */
  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }
}
